import { forwardRef, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MenuItem } from "~/common/mui";
import { SearchInputGroup } from "~/common/components/SearchInputGroup/SearchInputGroup";
import { useProductSuggestions } from "~/features/orders/common/hooks/useProductSuggestions";
import { useOptions } from "~/common/components/Select/SimpleAutocompleteSelect";
import {
  makeRenderOption,
  previewTableAutosuggestSx,
} from "~/features/bulkAddToCart/components/PreviewTable/PreviewTableMaterialIdInput";
import {
  QUERY_TYPE,
  type QueryType,
  type QueryTypeOption,
  type SearchByData,
} from "~/features/orders/common/queryTypes";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";
import { isInvoiceNumberEnabled, isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import SearchIcon from "@mui/icons-material/Search";

export interface OrderListSearchInputProps {
  className?: string;
  value: SearchByData;
  onChange: (data: SearchByData) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
  variant: SearchBoxVariant;
}

const isOrderList = (variant: SearchBoxVariant) => variant === SearchBoxVariant.ORDER;

const ORDER_OPTIONS: QueryTypeOption[] = [
  { value: QUERY_TYPE.PURCHASE_NO, labelKey: "searchByInput.queryType.po.orderlist" },
  { value: QUERY_TYPE.SALES_NO, labelKey: "searchByInput.queryType.so.orderlist" },
  { value: QUERY_TYPE.DESIGNATION, labelKey: "searchByInput.queryType.part" },
  { value: QUERY_TYPE.SAP_MATERIAL_NO, labelKey: "searchByInput.queryType.sap" },
  { value: QUERY_TYPE.CUSTOMER_MATERIAL_NO, labelKey: "searchByInput.queryType.customMaterialNumber" },
  { value: QUERY_TYPE.CUSTOMER_ORDER_NO, labelKey: "searchByInput.queryType.lineCustomerOrderId" },
  { value: QUERY_TYPE.INVOICE_NO, labelKey: "searchByInput.queryType.invoiceNumber" },
];

const QUOTE_OPTIONS: QueryTypeOption[] = [
  { value: QUERY_TYPE.PURCHASE_NO, labelKey: "searchByInput.queryType.po.quotelist" },
  { value: QUERY_TYPE.SALES_NO, labelKey: "searchByInput.queryType.so.quotelist" },
  { value: QUERY_TYPE.DESIGNATION, labelKey: "searchByInput.queryType.part" },
  { value: QUERY_TYPE.SAP_MATERIAL_NO, labelKey: "searchByInput.queryType.sap" },
  { value: QUERY_TYPE.CUSTOMER_MATERIAL_NO, labelKey: "searchByInput.queryType.customMaterialNumber" },
  { value: QUERY_TYPE.CUSTOMER_ORDER_NO, labelKey: "searchByInput.queryType.lineCustomerOrderId" },
  { value: QUERY_TYPE.INVOICE_NO, labelKey: "searchByInput.queryType.invoiceNumber" },
];

const useVisibleOptions = (variant: SearchBoxVariant) =>
  useMemo(() => {
    const base = isOrderList(variant) ? ORDER_OPTIONS : QUOTE_OPTIONS;
    return base.filter((o) => {
      if (o.value === QUERY_TYPE.CUSTOMER_ORDER_NO) {
        return isLineLevelCustomerOrderNumberEnabled();
      }
      if (o.value === QUERY_TYPE.INVOICE_NO) {
        return isOrderList(variant) && isInvoiceNumberEnabled();
      }
      return true;
    });
  }, [variant]);

export const makeLabelKeys = (variant: SearchBoxVariant): Record<string, string> => {
  const base = isOrderList(variant) ? ORDER_OPTIONS : QUOTE_OPTIONS;
  return Object.fromEntries(base.map((o) => [o.value, o.labelKey]));
};

export const OrderListSearchInput = forwardRef<HTMLInputElement, OrderListSearchInputProps>(
  ({ className, value, onChange, onBlur, error, helperText, disabled, variant }, ref) => {
    const { t } = useTranslation();
    const visibleOptions = useVisibleOptions(variant);
    const isDesignation = value.type === QUERY_TYPE.DESIGNATION;
    const { suggestions, loading, isOpen, open, close } = useProductSuggestions(value.query);
    const { options: productOptions, optionMap } = useOptions(suggestions, (item) => item.designation);

    const handleTypeChange = (newType: QueryType) => {
      onChange({ type: newType, query: newType !== value.type ? "" : value.query });
      onBlur?.();
    };

    return (
      <SearchInputGroup className={className}>
        {isDesignation ? (
          <SearchInputGroup.Autocomplete<string, false, false, true>
            options={productOptions}
            loading={loading}
            open={isOpen}
            onOpen={open}
            onClose={close}
            inputValue={value.query}
            onInputChange={(_, v) => onChange({ ...value, query: v })}
            onChange={(_, v) => {
              if (v === null) {
                onChange({ ...value, query: "" });
                return;
              }
              const product = optionMap[v as string];
              if (product?.designation) {
                onChange({ ...value, query: product.designation });
              }
            }}
            onBlur={onBlur}
            freeSolo
            filterOptions={(opts) => opts}
            renderOption={makeRenderOption(optionMap, previewTableAutosuggestSx.option, t)}
            placeholder={t("orderListPage.searchBtn")}
            data-test-id="autocomplete-designation"
            sx={{ flex: 3 }}
          />
        ) : (
          <SearchInputGroup.TextField
            value={value.query}
            onChange={(e) => onChange({ ...value, query: e.target.value })}
            onBlur={onBlur}
            inputRef={ref}
            error={error}
            helperText={helperText}
            disabled={disabled}
            fullWidth
            placeholder={t("orderListPage.searchBtn")}
            inputProps={{ "data-test-id": "search-by-input-query-input" }}
            sx={{ flex: 3 }}
          />
        )}
        <SearchInputGroup.Divider />
        <SearchInputGroup.Select
          value={value.type}
          onChange={(e) => handleTypeChange(e.target.value as QueryType)}
          data-test-id="search-by-input-by-dropdown"
          sx={{ flex: 2 }}
        >
          {visibleOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </MenuItem>
          ))}
        </SearchInputGroup.Select>
        <SearchInputGroup.IconButton type="submit" disabled={disabled}>
          <SearchIcon  />
        </SearchInputGroup.IconButton>
      </SearchInputGroup>
    );
  },
);
